import classNames from 'clsx'
import {
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  IS_JEST,
  PAGE_TYPE_CATEGORY,
  PAGE_TYPE_HOMEPAGE,
  PAGE_TYPE_PRODUCT,
  PAGE_TYPE_SEARCH_RESULTS,
  PAGES_WITHOUT_SEARCHBOX,
} from 'shared/consts'
import { EXPERIMENT_VARIATION } from 'shared/experiments/consts'
import { activateExperiment } from 'shared/experiments/utils/experiments'
import { updateNowDataSuccessAction } from 'shared/store/ducks/now/actions'
import { NOW_DATA_KEY } from 'shared/store/helper/consts'
import { type PageType } from 'types/cmsTypes'
import { type IState, type NowDataState } from 'types/redux'
import { type Tenant } from 'types/shopConfig'
import { type UserData } from 'views/components/organisms/CmsMobileMenu/types'
import NowGlobalBarContentBlueVariant from 'views/components/organisms/NowGlobalBarContentBlueVariant'
import NowGlobalBarContentGreenVariant from 'views/components/organisms/NowGlobalBarContentGreenVariant'
import Experiment from 'views/components/utils/optimizely/Experiment'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import buildClientAPI from 'views/providers/clientAPI'
import {
  fetchCityByZipCode,
  getExperimentNameForPageType,
} from 'views/utils/nowGlobalBar'
import './NowGlobalBar.scss'

interface INowGlobalBarProps {
  publicRuntimeConfig: {
    pageProperties: {
      pageType: PageType
    }
    publicConfig: {
      tenant: Tenant
    }
  }
}

const NowGlobalBar: (props: INowGlobalBarProps) => ReactElement = props => {
  const {
    publicRuntimeConfig: {
      pageProperties: { pageType },
      publicConfig: { tenant },
    },
  } = props
  const dispatch = useDispatch()
  const nowData: NowDataState = useSelector((state: IState) => state.nowData)

  const userData = useSelector((state: IState) => state.userSession) as UserData

  const [openNowZipModal, setOpenNowZipModal] = useState(false)
  const [zip, setZip] = useState(nowData?.zipCode ?? '')
  const [city, setCity] = useState(nowData?.city ?? '')
  const [isInitialZipCodeSet, setIsInitialZipCodeSet] = useState(false)
  const [isExperimentActivated, setIsExperimentActivated] = useState(false)
  const [isBillingAddressFetchedOnce, setIsBillingAddressFetchedOnce] =
    useState(false)

  const pagesWithGb = useMemo(
    () =>
      [
        PAGE_TYPE_HOMEPAGE,
        PAGE_TYPE_SEARCH_RESULTS,
        PAGE_TYPE_CATEGORY,
      ].includes(pageType),
    [pageType]
  )

  const storeCityData = useCallback(async () => {
    let cityToSet = ''
    const handleZipChange = async () => {
      if (nowData?.zipCode && pageType !== PAGE_TYPE_PRODUCT) {
        const { nowService } = buildClientAPI()

        return await nowService.fetchBasicData({
          zipCode: nowData.zipCode,
        })
      }
    }

    if (!nowData?.zipCode) {
      setCity(cityToSet)
    } else {
      cityToSet = (await fetchCityByZipCode(nowData?.zipCode)) ?? ''
      setCity(cityToSet)
    }
    const response = await handleZipChange()
    dispatch(
      updateNowDataSuccessAction({
        ...nowData,
        ...response,
        city: cityToSet,
        refreshNowData: !nowData?.refreshNowData,
        fetchCity: false,
        isNowCallNeeded: true,
      })
    )
  }, [dispatch, nowData, pageType])

  const setInitialState = useCallback(() => {
    if (isInitialZipCodeSet) {
      return
    }
    let defaultNowData = nowData

    if (!nowData && pagesWithGb) {
      const data = localStorage.getItem(NOW_DATA_KEY)
      defaultNowData = data ? JSON.parse(data) : null
    }

    setZip(defaultNowData?.zipCode ?? '')
    let fetchCity = true
    if (defaultNowData?.city) {
      setCity(defaultNowData?.city)
      fetchCity = false
      setIsInitialZipCodeSet(true)
    }

    dispatch(
      updateNowDataSuccessAction({
        ...defaultNowData,
        fetchCity,
      })
    )
  }, [dispatch, isInitialZipCodeSet, nowData, pagesWithGb])

  const setZipFromBillingAddress = useCallback(async () => {
    const data = localStorage.getItem('isZipCodeSetOnceFromBillingAddress')
    const isZipCodeSetOnceFromBillingAddress = data ? !!JSON.parse(data) : false

    if (!nowData?.zipCode && !isZipCodeSetOnceFromBillingAddress) {
      const clientApi = buildClientAPI()

      const billingAddress = await clientApi.addressService.getBillingAddress()

      if (billingAddress?.zip) {
        dispatch(
          updateNowDataSuccessAction({
            zipCode: billingAddress.zip,
          })
        )
        localStorage.setItem('isZipCodeSetOnceFromBillingAddress', 'true')
      }
    }
  }, [nowData?.zipCode, dispatch])

  useEffect(() => {
    if (
      userData.isLoggedIn &&
      isInitialZipCodeSet &&
      !isBillingAddressFetchedOnce
    ) {
      void setZipFromBillingAddress()
      setIsBillingAddressFetchedOnce(true)
    }
  }, [
    userData.isLoggedIn,
    setZipFromBillingAddress,
    isInitialZipCodeSet,
    isBillingAddressFetchedOnce,
  ])

  useEffect(() => {
    const experiment = getExperimentNameForPageType(pageType)

    if (experiment && isExperimentActivated) {
      if (
        pageType !== PAGE_TYPE_PRODUCT &&
        // avoid client call in Jest as no mock available
        !IS_JEST
      ) {
        void activateExperiment(experiment)
      }
      setIsExperimentActivated(true)
    }
  }, [isExperimentActivated, pageType])

  useEffect(() => {
    if (nowData?.zipCode || pagesWithGb) {
      setInitialState()
    } else {
      setIsInitialZipCodeSet(true)
    }
  }, [nowData?.zipCode, pageType, pagesWithGb, setInitialState])

  useEffect(() => {
    if (!isInitialZipCodeSet && nowData?.fetchCity) {
      setIsInitialZipCodeSet(true)
    }
  }, [isInitialZipCodeSet, nowData?.fetchCity, storeCityData])

  useEffect(() => {
    if (isInitialZipCodeSet && nowData?.zipCode && zip !== nowData?.zipCode) {
      setZip(nowData?.zipCode)
      void storeCityData()
    } else if (!nowData?.zipCode) {
      setZip('')
    }
  }, [isInitialZipCodeSet, nowData?.zipCode, storeCityData, zip])

  const pageHasNoSearchBarInHeader = PAGES_WITHOUT_SEARCHBOX.includes(pageType)
  const classes = classNames('c-NowGlobalBar', {
    'c-NowGlobalBar-with-search-box': !pageHasNoSearchBarInHeader,
    'c-NowGlobalBar-without-search-box': pageHasNoSearchBarInHeader,
  })

  return (
    <Experiment name={getExperimentNameForPageType(pageType)}>
      {({ isEnabled, variant }) =>
        isEnabled && (
          <div data-clientside-hook="h-NowGlobalBar" className={classes}>
            {variant === EXPERIMENT_VARIATION.V1 && (
              <NowGlobalBarContentBlueVariant
                zip={zip}
                city={city}
                openNowZipModal={openNowZipModal}
                setOpenNowZipModal={setOpenNowZipModal}
                pageType={pageType}
                tenant={tenant}
              />
            )}
            {variant === EXPERIMENT_VARIATION.V2 && (
              <NowGlobalBarContentGreenVariant
                zip={zip}
                city={city}
                openNowZipModal={openNowZipModal}
                setOpenNowZipModal={setOpenNowZipModal}
                userData={userData}
                pageType={pageType}
                tenant={tenant}
              />
            )}
          </div>
        )
      }
    </Experiment>
  )
}

export default withShopAndRuntimeConfig(NowGlobalBar)
